import logEvent from "@/common/Analytics";
import _ from "lodash";

import ProfilesApi from "@/common/http/ProfilesApi";
import ProjectsApi from "@/common/http/ProjectsApi";
import { HumanlyApi, toCandidateData, formatTranscriptMessages } from "@/common/humanly";
import { handleError } from "@/common/Errors";

const state = {
  // current candidate properties
  currentCandidate: {},
  candidateConnectors: [],
  candidateDedupeMessage: null,
  candidateTagOptions: [],
  conversationTranscript: null,
  isLoadingConversationTranscript: false,
  isLoadingHumanlyProfileDetails: false,
  isLoadingCandidateConnectors: false,
  isLoadingCurrentCandidate: false,
  isLoadingCurrentCandidateApplications: false,
  isLoadingCurrentCandidateCrm: false,
  isLoadingCurrentCandidateCompanies: false,
  isLoadingCurrentCandidateIntro: false,
  isLoadingCurrentCandidateOutreach: false,
  isLoadingCurrentCandidateSocialLinks: false,
  isLoadingCurrentCandidateSummary: false,
  isLoadingTagOptions: false,
  isRequestIntroEnabled: true,
  requestIntroConnectorsId: [],
  requestIntroFromAllConnectors: false,

  // current list properties in the store are for read only
  // to keep source of truth consistent- candidateList handles mutations, then synces state to store
  bulkSelectionAttributes: null, // store
  currentCandidateList: [],
  currentCandidateListCount: null,
  deselectedCandidates: [], // store
  selectedCandidates: [], // store
  numCandidatesWithPhone: 0, // store
};

const getters = {
  currentCandidate(state) {
    return state.currentCandidate;
  },
  isLoadingTagOptions(state) {
    return state.isLoadingTagOptions;
  },
  isLoadingCurrentCandidate(state) {
    return state.isLoadingCurrentCandidate;
  },
  candidateFromHumanly(state) {
    return state.currentCandidate.uuid !== null;
  },
  isLoadingHumanlyProfileDetails(state) {
    return state.isLoadingHumanlyProfileDetails;
  },
  conversationTranscript(state) {
    return state.conversationTranscript;
  },
  isLoadingConversationTranscript(state) {
    return state.isLoadingConversationTranscript;
  },
  isLoadingCurrentCandidateApplications(state) {
    return state.isLoadingCurrentCandidateApplications;
  },
  isLoadingCurrentCandidateCrm(state) {
    return state.isLoadingCurrentCandidateCrm;
  },
  isLoadingCurrentCandidateCompanies(state) {
    return state.isLoadingCurrentCandidateCompanies;
  },
  isLoadingCurrentCandidateOutreach(state) {
    return state.isLoadingCurrentCandidateOutreach;
  },
  isLoadingCurrentCandidateSummary(state) {
    return state.isLoadingCurrentCandidateSummary;
  },
  isLoadingCurrentCandidateSocialLinks(state) {
    return state.isLoadingCurrentCandidateSocialLinks;
  },
  isLoadingCandidateConnectors(state) {
    return state.isLoadingCandidateConnectors;
  },
  candidateTagOptions(state) {
    return state.candidateTagOptions;
  },
  candidateDedupeMessage(state) {
    return state.candidateDedupeMessage;
  },
  candidateConnectors(state) {
    return state.candidateConnectors;
  },
  selectedCandidates(state) {
    return state.selectedCandidates;
  },
  deselectedCandidates(state) {
    return state.deselectedCandidates;
  },
  currentCandidateList(state) {
    return state.currentCandidateList;
  },
  currentCandidateListCount(state) {
    return state.currentCandidateListCount;
  },
  isLoadingCurrentCandidateIntro(state) {
    return state.isLoadingCurrentCandidateIntro;
  },
  isLoadingCurrentCandidateHistory(state) {
    return state.isLoadingCurrentCandidateIntro || state.isLoadingCurrentCandidateOutreach;
  },
  isCurrentCandidateFullyLoaded(state, getters) {
    return (
      !state.isLoadingCurrentCandidate &&
      !state.isLoadingCurrentCandidateCrm &&
      !getters.isLoadingCurrentCandidateHistory &&
      !state.isLoadingCurrentCandidateApplications
    )
  },
  isRequestIntroEnabled(state) {
    return state.isRequestIntroEnabled;
  },
  requestIntroFromAllConnectors(state) {
    return state.requestIntroFromAllConnectors;
  },
  requestIntroConnectorsId(state) {
    return state.requestIntroConnectorsId;
  },
  bulkSelectionAttributes(state) {
    return state.bulkSelectionAttributes;
  },
  currentCandidateTouchpoints(state) {
    const candidate = state.currentCandidate;
    return _.compact(_.concat(
      candidate.introPacks || [],
      candidate.tasks || [],
      candidate.savedForLaters || [],
      candidate.atsCandidates || [],
      candidate.humanlyProfile?.conversations || [],
      candidate.humanlyProfile?.interviews || [],
      candidate.humanlyProfile?.events || [],
      candidate.humanlyProfile?.meetings || [],
      candidate.poolCandidateAssocs || []
    ));
  },
  selectedSMSRecipients(state) {
    return state.currentCandidateList
      .filter(candidate => state.selectedCandidates.some(selected => selected.profileId === candidate.profileId))
      .filter(candidate => candidate.phone && !candidate.phoneOptOut)
      .map(candidate => {
        const [first_name, ...last_name_parts] = candidate.name.split(" ");
        const last_name = last_name_parts.join(" ");

        return {
          first_name,
          last_name,
          phone: candidate.phone,
        };
      });
  },
  numCandidatesWithPhone(state) {
    return state.numCandidatesWithPhone;
  },
  numSelectedCandidatesWithPhone(state, getters) {
    if (!state.bulkSelectionAttributes || state.bulkSelectionAttributes.isIndividualCandidates) {
      return getters.selectedSMSRecipients.length;
    }

    const numDeselectedCandidatesWithPhone =
      state.currentCandidateList
        .filter(candidate => state.deselectedCandidates.some(deselected => deselected.profileId === candidate.profileId))
        .filter(candidate => candidate.phone && !candidate.phoneOptOut)
        .length;

    return state.numCandidatesWithPhone - numDeselectedCandidatesWithPhone;
  },
};

const actions = {
  // Refetch just the applications for the current candidate (e.g. after applying a stage action),
  // so the jobs tab reflects the server-side pending override without a full candidate reload.
  reloadProfileApplications({ commit, getters }) {
    const profileId = getters.currentCandidate?.profileId;
    if (!profileId) {
      return Promise.resolve();
    }

    commit("setIsLoadingCurrentCandidateApplications", true);
    // Swallow refetch errors: the stage action already succeeded server-side, so a failed
    // refresh just leaves the optimistic badge in place rather than surfacing a second error.
    // Callers invoke this fire-and-forget, so it must not reject.
    return ProfilesApi.showProfileApplications({ profileId }).then(({ data }) => {
      // Drop the response if the user navigated to a different candidate while it was in flight,
      // otherwise this candidate's applications get merged onto the newly-selected one.
      if (getters.currentCandidate?.profileId !== profileId) {
        return;
      }
      commit("mergeCurrentCandidate", data.profile);
    }).catch(() => {}).finally(() => {
      commit("setIsLoadingCurrentCandidateApplications", false);
    });
  },
  fetchCurrentCandidate({ state, getters, commit, dispatch }, { profileId, tfId, projectId, accordWizardUuid, showSummary = false, status, sourcingTab }) {
    commit("setIsLoadingCurrentCandidate", true);
    commit("clearCurrentCandidate");

    // Look up by profileId when present; fall back to tfId for pure recs (no profileId).
    // Using `c.profileId == profileId` with profileId=null/undefined would match the first
    // rec with a null profileId rather than the clicked one (causing wrong avatar).
    const listCandidate = profileId
      ? state.currentCandidateList.find(c => c.profileId == profileId)
      : (tfId ? state.currentCandidateList.find(c => c.tfId === tfId) : null);
    const hasListData = !!listCandidate;
    if (hasListData) {
      commit("mergeCurrentCandidate", listCandidate);
    }

    if (showSummary) {
      commit("setIsLoadingCurrentCandidateSummary", true);
      ProfilesApi.showProfileSummary({ profileId, tfId, projectId, accordWizardUuid }).then(({ data }) => {
        commit("mergeCurrentCandidate", data.profile);
      }).finally(() => {
        commit("setIsLoadingCurrentCandidateSummary", false);
      })
    }
    if (profileId) {
      commit("setIsLoadingCurrentCandidateApplications", true);
      commit("setIsLoadingCurrentCandidateCrm", true);
      commit("setIsLoadingCurrentCandidateCompanies", true);
      commit("setIsLoadingCurrentCandidateIntro", true);
      commit("setIsLoadingCurrentCandidateOutreach", true);
      commit("setIsLoadingCurrentCandidateSocialLinks", true);

      ProfilesApi.showProfile({ profileId, projectId, accordWizardUuid, status, sourcingTab, ...(hasListData && { lightweight: true, tfId: listCandidate.tfId }) }).then(async ({ data }) => {
        commit("mergeCurrentCandidate", data.profile);
        if (data.profile.profileContactUuid) {
          await dispatch("fetchCandidateFromHumanly", { team_profile_id: data.profile.id });
        }
      }).finally(() => {
        commit("setIsLoadingCurrentCandidate", false);
      });
      if (listCandidate?.employments?.length > 0) {
        ProfilesApi.showProfileCompanies({ profileId, tfId: listCandidate.tfId }).then(({ data }) => {
          commit("mergeCurrentCandidate", data.profile);
        }).finally(() => {
          commit("setIsLoadingCurrentCandidateCompanies", false);
        });
      } else {
        commit("setIsLoadingCurrentCandidateCompanies", false);
      }
      ProfilesApi.showProfileCrm({ profileId, projectId, accordWizardUuid }).then(({ data }) => {
        commit("mergeCurrentCandidate", data.profile);
      }).finally(() => {
        commit("setIsLoadingCurrentCandidateCrm", false);
      });
      ProfilesApi.showProfileOutreach({ profileId, projectId, accordWizardUuid }).then(({ data }) => {
        commit("mergeCurrentCandidate", data.profile);
      }).finally(() => {
        commit("setIsLoadingCurrentCandidateOutreach", false);
      });
      ProfilesApi.profileIntros({ profileId, projectId, accordWizardUuid }).then(({ data }) => {
        commit("mergeCurrentCandidate", data.profile);
      }).finally(() => {
        commit("setIsLoadingCurrentCandidateIntro", false);
      });

      if (getters.currentTeam.socialLinksEnabled) {
        ProfilesApi.socialLinks(profileId).then(({ data }) => {
          const socialLinks = data?.socialLinks;
          if (socialLinks) {
            commit("mergeCurrentCandidate", { socialLinks });
          }
        }).finally(() => {
          commit("setIsLoadingCurrentCandidateSocialLinks", false);
        });
      } else {
        commit("setIsLoadingCurrentCandidateSocialLinks", false);
      }

      if (getters.currentUser.applicationsTabInSidebarEnabled) {
        ProfilesApi.showProfileApplications({ profileId, projectId, accordWizardUuid }).then(({ data }) => {
          commit("mergeCurrentCandidate", data.profile);
        }).finally(() => {
          commit("setIsLoadingCurrentCandidateApplications", false);
        });
      } else {
        commit("setIsLoadingCurrentCandidateApplications", false);
      }
    } else {
      ProfilesApi.showRecommendation({ projectId, tfId, accordWizardUuid }).then(({ data }) => {
        commit("mergeCurrentCandidate", data.profile);
      }).finally(() => {
        commit("setIsLoadingCurrentCandidate", false);
      });
    }
  },

  async fetchCandidateFromHumanly({ commit }, { team_profile_id }) {
    commit("setIsLoadingHumanlyProfileDetails", true);

    try {
      const [
        { resume = null },
        { conversations = [] },
        { interviews = [] },
        { events = [] },
        { meetings = [] },
        { candidacies = [] },
      ] = await Promise.all([
        HumanlyApi.fetchResume({ team_profile_id }).catch(()=>({ resume: [] })),
        HumanlyApi.fetchConversations({ team_profile_id }).catch(()=>({ conversations: [] })),
        HumanlyApi.fetchInterviews({ team_profile_id }).catch(()=>({ interviews: [] })),
        HumanlyApi.fetchContactEvents({ team_profile_id }).catch(()=>({ events: [] })),
        HumanlyApi.fetchMeetings({ team_profile_id }).catch(()=>({ meetings: [] })),
        HumanlyApi.fetchCandidacies({ team_profile_id }).catch(() => ({ candidacies: [] })),
      ]);

      const candidate_data = toCandidateData(resume, conversations, interviews, events, meetings, candidacies);
      commit("mergeCurrentCandidate", candidate_data);
    } catch (error) {
      handleError(error, { context: "fetchCandidateFromHumanly", team_profile_id });
    } finally {
      commit("setIsLoadingHumanlyProfileDetails", false);
    }
  },

  async fetchConversationTranscript({ commit }, { id }) {
    commit("setIsLoadingConversationTranscript", true);
    commit("setConversationTranscript", null);

    try {
      const data = await HumanlyApi.fetchTranscript({
        id,
      });
      commit("setConversationTranscript", {
        messages: formatTranscriptMessages(data.messages),
        playbook_name: data.playbook_name,
        export_link: data.export_link,
      });
    } catch (error) {
      handleError(error, { context: "fetchConversationTranscript", id });
    } finally {
      commit("setIsLoadingConversationTranscript", false);
    }
  },

  async updateCandidateTags(context, { candidate, tags, bulkSelectionAttributes }) {
    let params = {
      tags,
      bulkSelectionAttributes,
      profileId: candidate?.profileId,
    };
    ProfilesApi.updateProfileTags(params);
  },
  async updateCandidate({ commit }, candidate) {
    await ProfilesApi.updateProfile({ ...candidate });

    if (candidate.profileContactUuid) {
      const [firstName, ...lastNameParts] = (candidate.name || "").split(" ");
      const lastName = lastNameParts.join(" ");

      await HumanlyApi.updateContactsRequest(candidate.profileContactUuid, {
        first_name: firstName,
        last_name: lastName,
        email: candidate.email,
        phone: candidate.phone,
      });
    }

    commit("mergeCurrentCandidate", candidate)
    commit("updateCandidateInList", candidate)
  },
  async fetchCandidateDedupeMessage({ commit }, { profileId, tfId, projectId }) {
    const { data } = await ProfilesApi.dedupeMessage({ profileId, tfId, projectId });
    commit("setCandidateDedupeMessage", data.dedupeMessage);
  },
  async fetchCandidateConnectors({ commit }, { profileId }) {
    commit("setIsLoadingCandidateConnectors", true);
    const { data } = await ProfilesApi.candidateConnectors({ profileId });
    commit("setCandidateConnectors", data.connectors);
    commit("setIsLoadingCandidateConnectors", false);
  },
  async addCandidateReminder(context, { candidate, bulkSelectionAttributes, remindAt, reason }) {
    logEvent("candidate-action", { action: "remind-me-later" });
    const params = {
      bulkSelectionAttributes,
      profileId: candidate?.profileId,
      remindAt,
      reason,
    }
    const { data } = await ProfilesApi.createReminder(params);
    if (candidate?.reminders) {
      candidate.reminders.push(data.reminder);
    }
  },
  async deleteCandidateReminder(context, { candidate, reminder }) {
    await ProfilesApi.deleteReminder(reminder.id);

    const idx = candidate.reminders.findIndex(r => r.id == reminder.id);
    candidate.reminders.splice(idx, 1);
  },
  async addCandidateToProject(context, { candidate, projectId, bulkSelectionAttributes, isRerouteFromReview, remindDays, recommendationsProjectId }) {
    logEvent("candidate-action", { action: "add-to-project" });

    const { data } = await ProjectsApi.addToProject({
      bulkSelectionAttributes,
      isRerouteFromReview,
      profileId: candidate?.profileId,
      tfId: candidate?.tfId,
      projectId,
      recommendationsProjectId,
      remindDays,
    });
    if (candidate) {
      const candidateHasProject = candidate.projectProfiles?.map(pp => pp.projectId).includes(projectId);
      if (!candidate.projectProfiles) {
        candidate.projectProfiles = [data.projectProfile];
      } else if (!candidateHasProject) {
        candidate.projectProfiles.push(data.projectProfile);
      }
    }
  },
  async removeBulkCandidatesFromProject(context, { project, bulkSelectionAttributes }) {
    const params = {
      bulkSelectionAttributes,
      projectId: project.id,
    }

    await ProjectsApi.removeCandidatesBulk(params);
  },
  async fetchTagOptions({ commit }) {
    commit("setIsLoadingTagOptions", true);
    const { data } = await ProfilesApi.tagOptions();
    commit("setCandidateTagOptions", data.tagOptions);
    commit("setIsLoadingTagOptions", false);
  },
  async removeCandidateFromProject({ commit }, { candidate, projectId, projectProfileId }) {
    if (projectProfileId) {
      ProjectsApi.removeFromProject(projectProfileId);
    } else {
      const { data } = await ProjectsApi.removeCandidate({ profileId: candidate.profileId, projectId });
      projectProfileId = data.projectProfileId;
    }

    commit("removeProjectProfile", { candidate, projectProfileId });
  },
  async saveCandidateWatched(state, { candidate, watched, bulkSelectionAttributes }) {
    logEvent("candidate-action", { action: "watch" });
    ProfilesApi.watchProfile({
      profileId: candidate?.profileId,
      bulkSelectionAttributes,
      watch: watched,
    });

    if (candidate) {
      candidate.watched = watched;
    }
  },
};

const mutations = {
  setIsLoadingTagOptions(state, value) {
    state.isLoadingTagOptions = value;
  },
  mergeCurrentCandidate(state, candidate) {
    // _ for deep merge
    // Object.assign since vue isn't rerendering when nested properties are changed
    // Preserve existing avatarUrl if the new data doesn't provide one
    const merged = _.mergeWith({}, state.currentCandidate, candidate, (objValue, srcValue) => {
      if (Array.isArray(srcValue)) return srcValue;
    });
    if (!candidate.avatarUrl && state.currentCandidate.avatarUrl) {
      merged.avatarUrl = state.currentCandidate.avatarUrl;
    }
    state.currentCandidate = Object.assign({}, merged);
  },
  updateCandidateInList(state, candidate) {
    const index = state.currentCandidateList.findIndex(c => c.profileId === candidate.profileId);
    if (index !== -1) {
      const updatedCandidate = _.merge({}, state.currentCandidateList[index], candidate);
      state.currentCandidateList.splice(index, 1, updatedCandidate);
    }
  },
  setCandidateTagOptions(state, tagOptions) {
    state.candidateTagOptions = tagOptions;
  },
  addTagOptionToCandidateTagOptions(state, tag) {
    if (!state.candidateTagOptions.includes(tag)) {
      state.candidateTagOptions.push(tag);
    }
  },
  setIsLoadingHumanlyProfileDetails(state, value) {
    state.isLoadingHumanlyProfileDetails = value;
  },
  setConversationTranscript(state, value) {
    state.conversationTranscript = value;
  },
  setIsLoadingConversationTranscript(state, value) {
    state.isLoadingConversationTranscript = value;
  },
  clearCurrentCandidate(state) {
    state.currentCandidate = {};
  },
  setCandidateDedupeMessage(state, value) {
    state.candidateDedupeMessage = value;
  },
  setCandidateConnectors(state, value) {
    state.candidateConnectors = value;
  },
  setIsLoadingCandidateConnectors(state, value) {
    state.isLoadingCandidateConnectors = value;
  },
  setIsLoadingCurrentCandidate(state, value) {
    state.isLoadingCurrentCandidate = value;
  },
  setIsLoadingCurrentCandidateApplications(state, value) {
    state.isLoadingCurrentCandidateApplications = value;
  },
  setIsLoadingCurrentCandidateCrm(state, value) {
    state.isLoadingCurrentCandidateCrm = value;
  },
  setIsLoadingCurrentCandidateSummary(state, value) {
    state.isLoadingCurrentCandidateSummary = value;
  },
  setIsLoadingCurrentCandidateCompanies(state, value) {
    state.isLoadingCurrentCandidateCompanies = value;
  },
  setIsLoadingCurrentCandidateIntro(state, value) {
    state.isLoadingCurrentCandidateIntro = value;
  },
  setIsLoadingCurrentCandidateOutreach(state, value) {
    state.isLoadingCurrentCandidateOutreach = value;
  },
  setIsLoadingCurrentCandidateSocialLinks(state, value) {
    state.isLoadingCurrentCandidateSocialLinks = value;
  },
  removeProjectProfile(state, { candidate, projectProfileId }) {
    const idx = candidate.projectProfiles?.findIndex(p => p.id == projectProfileId);
    if (idx > -1) {
      candidate.projectProfiles.splice(idx, 1);
    }
  },
  setIsIntrosEnabledByUser(state, value) {
    state.isRequestIntroEnabled = value;
  },
  setRequestIntroFromAllConnectors(state, value) {
    state.requestIntroFromAllConnectors = value;
  },
  setRequestIntroConnectorsId(state, value) {
    state.requestIntroConnectorsId = value;
  },
  setBulkSelectionAttributes(state, value) {
    state.bulkSelectionAttributes = value;
  },
  setSelectedCandidates(state, value) {
    state.selectedCandidates = value.map(c => {
      return { profileId: c.profileId, tfId: c.tfId, projectIdsSavedForLater: c.projectIdsSavedForLater };
    });
  },
  setDeselectedCandidates(state, value) {
    state.deselectedCandidates = value.map(c => {
      return { profileId: c.profileId, tfId: c.tfId };
    });
  },
  setCurrentCandidateList(state, candidates) {
    state.currentCandidateList = candidates;
  },
  setCurrentCandidateListCount(state, newValue) {
    state.currentCandidateListCount = newValue;
  },
  setNumCandidatesWithPhone(state, newValue) {
    state.numCandidatesWithPhone = newValue;
  },
};

export default {
  state,
  actions,
  mutations,
  getters,
};
